import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileDown,
  FileUp,
  Calculator,
  BarChart3,
  Users,
  FolderOpen,
  X,
  BookOpen,
  Receipt,
  ArrowLeftRight,
  UserCheck,
  Building2,
  Upload,
  Scale,
  TrendingUp,
  CalendarClock,
  AlertTriangle,
  Inbox,
  CheckSquare,
  Banknote,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useConfig } from '../hooks/useConfig';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const ROUTE_FEATURE_MAP: Record<string, string> = {
  '/funcionarios': 'funcionarios',
  '/impostos': 'impostos',
  '/contas-pagar': 'contas-pagar',
  '/contas-receber': 'contas-receber',
};

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/contas', label: 'Contas', icon: BookOpen },
  { to: '/lancamentos', label: 'Lançamentos', icon: Receipt },
  { to: '/importar', label: 'Importar', icon: Upload },
  { to: '/duplicatas', label: 'Duplicatas', icon: AlertTriangle },
  { to: '/pendentes', label: 'Pendentes', icon: Inbox },
  { to: '/tarefas', label: 'Tarefas', icon: CheckSquare },
  { to: '/banco-mcp', label: 'Banco MCP', icon: Banknote },
  { to: '/fluxo-de-caixa', label: 'Fluxo de Caixa', icon: ArrowLeftRight },
  { to: '/dre', label: 'DRE', icon: BarChart3 },
  { to: '/projecao', label: 'Projeção', icon: TrendingUp },
  { to: '/fluxo-projetado', label: 'Fluxo Projetado', icon: TrendingUp },
  { to: '/conciliacao', label: 'Conciliação', icon: Scale },
  { to: '/contas-pagar', label: 'Passivo', icon: FileDown },
  { to: '/contas-receber', label: 'Contas a Receber', icon: FileUp },
  { to: '/impostos', label: 'Impostos', icon: Calculator },
  { to: '/relatorios', label: 'Relatórios', icon: BarChart3 },
  { to: '/funcionarios', label: 'Funcionários', icon: UserCheck },
  { to: '/clientes', label: 'Clientes', icon: Building2 },
  { to: '/notas-fiscais', label: 'Notas Fiscais', icon: Receipt },
  { to: '/notas-fiscais-fornecedor', label: 'NFs Fornecedor', icon: Receipt },
  { to: '/documentos', label: 'Documentos', icon: FolderOpen },
  { to: '/contas-periodicas', label: 'Contas Periódicas', icon: CalendarClock },
];

// Portais oficiais do governo (abrem em nova aba)
const externalLinks = [
  { href: 'https://login.esocial.gov.br/login.aspx', label: 'eSocial' },
  { href: 'https://cav.receita.fazenda.gov.br/', label: 'e-CAC' },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { isAdmin } = useAuth();
  const { config, hasFeature } = useConfig();

  const filteredNavItems = navItems.filter((item) => {
    const feature = ROUTE_FEATURE_MAP[item.to];
    return !feature || hasFeature(feature);
  });

  const allNavItems = isAdmin
    ? [...filteredNavItems, { to: '/users', label: 'Usuários', icon: Users }]
    : filteredNavItems;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white
          transform transition-transform duration-200 ease-in-out
          md:relative md:translate-x-0
          flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-700 flex-shrink-0">
          <h1 className="text-lg font-bold">{config.name}</h1>
          <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto mt-4 px-3 space-y-1">
          {allNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
              end={to === '/'}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {label}
            </NavLink>
          ))}

          {/* Portais oficiais do governo (externos) */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Portais Gov
            </p>
            {externalLinks.map(({ href, label }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
              >
                <span className="flex items-center gap-3">
                  <ExternalLink className="h-5 w-5 flex-shrink-0" />
                  {label}
                </span>
              </a>
            ))}
          </div>
        </nav>

        <div className="px-6 py-4 border-t border-slate-700 flex-shrink-0">
          <p className="text-xs text-slate-500">{config.name}</p>
        </div>
      </aside>
    </>
  );
};
